import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/index.js';
import { API_BASE_URL } from '../config.js';
import { useToast } from '../context/ToastContext.js';
import { useConfirm } from './ui/useConfirm.js';
import AthleteRoutineTracker from './AthleteRoutineTracker.js';
import './RosterOverview.css';

const money = (n) => `$${Number(n || 0).toFixed(2)}`;

// The unified roster: directory + operational status (waivers, balance, insurance,
// parent linkage/activation, enrollment, contact) in one place. Replaced the old
// plain roster plus the separate "Ops & Compliance" and "Roster Intake" screens.
const FILTERS = [
  { key: 'all', label: 'Athletes', tone: '', tile: t => t.athletes || 0, test: () => true },
  { key: 'unsigned', label: 'Unsigned waivers', tone: 'bad', tile: t => t.unsigned || 0, test: r => r.waivers_missing > 0 },
  { key: 'balance', label: 'Has balance', tone: 'bad', tile: t => money(t.outstanding), test: r => r.outstanding > 0 },
  { key: 'no_insurance', label: 'No insurance', tone: 'warn', tile: t => t.no_insurance || 0, test: r => !r.insurance_on_file },
  { key: 'no_parent', label: 'No parent', tone: 'warn', tile: t => t.no_parent || 0, test: r => r.parent_count === 0 },
  { key: 'not_activated', label: 'Not activated', tone: 'warn', tile: t => t.parent_not_activated || 0, test: r => r.parent_count > 0 && !r.parent_activated },
  { key: 'not_enrolled', label: 'Not enrolled', tone: 'warn', tile: t => t.not_enrolled || 0, test: r => r.enrolled === 0 },
];

function RosterOverview({ onDelete }) {
  const navigate = useNavigate();
  const toast = useToast();
  const [confirm, confirmModal] = useConfirm();

  const [data, setData] = useState({ rows: [], totals: {} });
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('status'); // 'status' | 'directory'
  const [filter, setFilter] = useState('all');
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [showRoutineFor, setShowRoutineFor] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get(`${API_BASE_URL}/api/admin/roster-overview`);
      setData(res.data || { rows: [], totals: {} });
    } catch (err) {
      toast.error('Could not load roster');
    } finally { setLoading(false); }
  }, [toast]);
  useEffect(() => { load(); }, [load]);

  const { rows = [], totals = {} } = data;

  const filtered = useMemo(() => {
    const f = FILTERS.find(x => x.key === filter) || FILTERS[0];
    return rows.filter(r => {
      if (q && !r.name.toLowerCase().includes(q.toLowerCase())) return false;
      return f.test(r);
    });
  }, [rows, filter, q]);

  const allSelected = filtered.length > 0 && filtered.every(r => selected.has(r.athlete_id));
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(filtered.map(r => r.athlete_id)));
  const toggleOne = (id) => setSelected(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const setFilterAndClear = (key) => { setFilter(key); setSelected(new Set()); };

  const remind = async (athleteIds) => {
    setBusy(true);
    try {
      const body = athleteIds ? { athlete_ids: athleteIds } : {};
      const { data: r } = await api.post(`${API_BASE_URL}/api/waivers/remind-bulk`, body);
      const skipped = (r.skipped_no_parent || []).length;
      toast.success(`Reminded ${r.athletes_reminded} athlete(s)${skipped ? `, ${skipped} skipped (no parent)` : ''}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Reminder failed');
    } finally { setBusy(false); }
  };

  const invite = async (parentId) => {
    setBusy(true);
    try {
      const res = await api.post(`${API_BASE_URL}/api/admin/parents/${parentId}/activation-link`);
      await navigator.clipboard?.writeText(res.data.link).catch(() => {});
      toast.success(res.data.emailed
        ? `Activation link emailed to ${res.data.parent_email} (also copied)`
        : 'Activation link copied to clipboard');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to create activation link');
    } finally { setBusy(false); }
  };

  // Open a signed waiver PDF (streamed from R2 via the authenticated endpoint)
  const viewWaiver = async (submissionId) => {
    try {
      const res = await api.get(`${API_BASE_URL}/api/forms/submissions/${submissionId}/signed-pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.target = '_blank'; a.rel = 'noopener';
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      toast.error('Could not open the signed waiver');
    }
  };

  const removeAthlete = async (r) => {
    if (await confirm({
      title: 'Delete athlete?',
      message: `This permanently removes ${r.name} and their records. This cannot be undone.`,
      confirmText: 'Delete',
      danger: true,
    })) {
      await onDelete?.(r.athlete_id);
      load();
    }
  };

  if (loading) return <div className="ro-wrap"><div className="ro-loading">Loading roster…</div></div>;

  return (
    <div className="ro-wrap">
      {confirmModal}

      <div className="ro-head">
        <h1>👥 Roster</h1>
        <div className="ro-head-actions">
          <div className="ro-viewtoggle">
            <button className={view === 'status' ? 'active' : ''} onClick={() => setView('status')}>Status</button>
            <button className={view === 'directory' ? 'active' : ''} onClick={() => setView('directory')}>Directory</button>
          </div>
          <button className="ro-btn primary" onClick={() => navigate('/create-athlete')}>➕ Add Athlete</button>
        </div>
      </div>

      {/* Stat tiles double as filters */}
      <div className="ro-tiles">
        {FILTERS.map(f => (
          <button
            key={f.key}
            className={`ro-tile ${filter === f.key ? 'active' : ''} ${(Number(f.tile(totals)) > 0 || typeof f.tile(totals) === 'string') && f.tone ? f.tone : ''}`}
            onClick={() => setFilterAndClear(f.key)}
          >
            <span className="ro-tile-v">{f.tile(totals)}</span>
            <span className="ro-tile-l">{f.label}</span>
          </button>
        ))}
      </div>

      <div className="ro-controls">
        <input className="ro-search" placeholder="🔎 Search athletes…" value={q} onChange={e => setQ(e.target.value)} />
        <div className="ro-bulk">
          <button className="ro-btn" disabled={busy} onClick={() => remind(selected.size ? [...selected] : null)}>
            📧 {selected.size ? `Remind ${selected.size} selected` : 'Remind all unsigned'}
          </button>
          {selected.size === 1 && (
            <button className="ro-btn ghost" onClick={() => setShowRoutineFor([...selected][0])}>📋 Log Routines</button>
          )}
          <span className="ro-count">{filtered.length} shown</span>
        </div>
      </div>

      <div className="ro-table-wrap">
        <table className="ro-table">
          <thead>
            {view === 'status' ? (
              <tr>
                <th><input type="checkbox" checked={allSelected} onChange={toggleAll} /></th>
                <th>Athlete</th><th>Classes</th><th>Waivers</th><th>Balance</th>
                <th>Insurance</th><th>Parent</th><th>Contact</th><th></th>
              </tr>
            ) : (
              <tr>
                <th><input type="checkbox" checked={allSelected} onChange={toggleAll} /></th>
                <th>Athlete</th><th>DOB</th><th>Height</th><th>Weight</th>
                <th>Email</th><th>Phone</th><th>Emergency</th><th></th>
              </tr>
            )}
          </thead>
          <tbody>
            {filtered.map(r => (
              <tr key={r.athlete_id} className={!r.is_active ? 'inactive' : ''}>
                <td><input type="checkbox" checked={selected.has(r.athlete_id)} onChange={() => toggleOne(r.athlete_id)} /></td>
                <td className="ro-name" onClick={() => navigate(`/athlete-card/${r.athlete_id}`)}>
                  {r.name}{!r.is_active && <span className="ro-inactive-tag">inactive</span>}
                </td>

                {view === 'status' ? (
                  <>
                    <td>{r.classes}</td>
                    <td>
                      {r.waivers_required === 0 ? <span className="ro-muted">—</span>
                        : r.waivers_missing > 0 ? <span className="ro-badge bad">{r.waivers_missing} missing</span>
                        : <span className="ro-badge good">✓ signed</span>}
                      {(r.signed_waivers || []).map(sw => (
                        <button key={sw.id} className="ro-waiver-view" title={`View signed: ${sw.form_name}`}
                          onClick={() => viewWaiver(sw.id)}>📄</button>
                      ))}
                    </td>
                    <td className={r.outstanding > 0 ? 'ro-due' : ''}>{r.outstanding > 0 ? money(r.outstanding) : <span className="ro-muted">—</span>}</td>
                    <td><span className={`ro-badge ${r.insurance_on_file ? 'good' : 'bad'}`}>{r.insurance_on_file ? '✓ on file' : '✗ none'}</span></td>
                    <td>
                      {r.parent_count === 0 ? <span className="ro-badge bad">none</span>
                        : r.parent_activated ? <span className="ro-badge good">✓ {r.parent_name}</span>
                        : <span className="ro-badge warn">pending · {r.parent_name}</span>}
                    </td>
                    <td>
                      {r.missing_contact ? <span className="ro-badge warn">no email/phone</span>
                        : <span className="ro-badge good">✓ reachable</span>}
                    </td>
                  </>
                ) : (
                  <>
                    <td className="ro-muted">{r.dob || '—'}</td>
                    <td className="ro-muted">{r.height || '—'}</td>
                    <td className="ro-muted">{r.weight || '—'}</td>
                    <td className="ro-muted">{r.email || '—'}</td>
                    <td className="ro-muted">{r.phone || '—'}</td>
                    <td className="ro-muted">
                      {r.emergency_contact_name || '—'}{r.emergency_contact_phone ? ` · ${r.emergency_contact_phone}` : ''}
                    </td>
                  </>
                )}

                <td className="ro-actions">
                  {r.waivers_missing > 0 && r.parent_count > 0 && (
                    <button className="ro-mini" disabled={busy} onClick={() => remind([r.athlete_id])}>Remind</button>
                  )}
                  {r.parent_id && !r.parent_activated && (
                    <button className="ro-mini" disabled={busy} onClick={() => invite(r.parent_id)}>Invite</button>
                  )}
                  <button className="ro-mini ghost" onClick={() => navigate(`/athlete-card/${r.athlete_id}`)}>Card</button>
                  <button className="ro-mini ghost" onClick={() => navigate(`/edit-athlete/${r.athlete_id}`)}>Edit</button>
                  <button className="ro-mini danger" onClick={() => removeAthlete(r)}>Delete</button>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={9} className="ro-empty">No athletes match this filter.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {showRoutineFor && (
        <div className="ro-routine">
          <div className="ro-routine-head">
            <h3>Log Routines</h3>
            <button className="ro-mini ghost" onClick={() => setShowRoutineFor(null)}>Close</button>
          </div>
          <AthleteRoutineTracker athleteId={showRoutineFor} />
        </div>
      )}
    </div>
  );
}

export default RosterOverview;
